// App
import type React from 'react';
import { useEffect, useState } from 'react';

// Misc / utils
import VALUE_CONSTANTS from 'app/shared/constants/ValueConstants';
import * as S from './styles';

type PriceInputProps = {
  'data-name'?: string;
  'data-testid'?: string;
  ariaLabel: string;
  commitOnChange?: boolean;
  disabled?: boolean;
  handleSliderValueChange: (value: number, options: { isValueUnset: boolean }) => void;
  id: string;
  isMaxInput?: boolean;
  isMinInput?: boolean;
  isValueUnset?: boolean;
  name: string;
  placeholder: string;
  value: number;
};

const PriceInput = ({
  'data-name': dataName,
  'data-testid': dataTestId,
  ariaLabel,
  commitOnChange = false,
  disabled,
  handleSliderValueChange,
  id,
  isMaxInput,
  isMinInput,
  isValueUnset,
  name,
  placeholder,
  value,
}: PriceInputProps) => {
  const ONE_TO_HUNDRED_SCALE = 1 / VALUE_CONSTANTS.PRICE_FILTER_INCREMENT;
  const INPUT_MIN = 0;
  const INPUT_MAX = 5000;
  const isCurrentValueUnset =
    isValueUnset ?? Boolean((isMinInput && value === INPUT_MIN) || (isMaxInput && value === INPUT_MAX));
  const [isFocused, setIsFocused] = useState(false);
  const [localInputValue, setLocalInputValue] = useState(value);
  const [isLocalInputEmpty, setIsLocalInputEmpty] = useState(isCurrentValueUnset);
  const isInputValueInRange = (inputValue: number) => inputValue >= INPUT_MIN && inputValue <= INPUT_MAX;

  // Updates to the slider should also update the input
  useEffect(() => {
    setLocalInputValue(value);
    setIsLocalInputEmpty(isCurrentValueUnset);
  }, [isCurrentValueUnset, value]);

  const handleInputFocus = () => {
    setIsFocused(true);
  };

  const resetLocalInputValue = () => {
    setLocalInputValue(value);
    setIsLocalInputEmpty(isCurrentValueUnset);
  };

  const commitLocalInputValue = () => {
    if (!isInputValueInRange(localInputValue)) {
      resetLocalInputValue();
      return;
    }

    if (localInputValue !== value || isLocalInputEmpty !== isCurrentValueUnset) {
      handleSliderValueChange(Math.floor(localInputValue * ONE_TO_HUNDRED_SCALE), {
        isValueUnset: isLocalInputEmpty,
      });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const keyCode = e.keyCode;
    const enterKeyPressed = keyCode === VALUE_CONSTANTS.ENTER_KEYCODE;

    if (enterKeyPressed) {
      commitLocalInputValue();
    }
  };

  const handleInputBlur = () => {
    commitLocalInputValue();
    setIsFocused(false);
  };

  const handleLocalInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isNextInputEmpty = e.target.value === '';
    const nextLocalInputValue = isNextInputEmpty ? (isMaxInput ? INPUT_MAX : INPUT_MIN) : Number(e.target.value);

    setIsLocalInputEmpty(isNextInputEmpty);
    setLocalInputValue(nextLocalInputValue);

    if (commitOnChange && isInputValueInRange(nextLocalInputValue)) {
      handleSliderValueChange(Math.floor(nextLocalInputValue * ONE_TO_HUNDRED_SCALE), {
        isValueUnset: isNextInputEmpty,
      });
    }
  };

  return (
    <S.NumberInputContainer>
      {isMinInput && (isFocused || !isLocalInputEmpty) && <span aria-hidden="true">$</span>}
      {isMaxInput && (isFocused || !isLocalInputEmpty) && <span aria-hidden="true">$</span>}
      <S.NumberInput
        aria-label={ariaLabel}
        disabled={disabled}
        data-name={dataName}
        data-testid={dataTestId}
        id={id}
        max={INPUT_MAX}
        min={INPUT_MIN}
        name={name}
        onChange={handleLocalInputChange}
        onKeyDown={handleKeyDown}
        onBlur={handleInputBlur}
        onFocus={handleInputFocus}
        placeholder={isFocused ? '' : placeholder}
        step="100"
        type="number"
        value={isLocalInputEmpty ? '' : localInputValue}
      />
    </S.NumberInputContainer>
  );
};

export default PriceInput;
